// The ambient light's direction, for the cloud shaders (the dome's layers in sky.ts, the volume in cloudVolume.ts, the cells in cloudCells.ts).
//
// skyLight.ts `field` makes nine coefficients of spherical harmonics for the light on a face turned any way. The lit materials take them through a three LightProbe.
// The cloud shaders do not use three's lights: each takes one ambient colour (uAmb, from the sky light) and multiplies it by a factor of its own. Here that colour
// is multiplied by the field's light on a face turned toward the cloud's normal, relative to a face turned up (the coefficients are divided by the up face's light,
// per channel, before they arrive), so uAmb keeps its level and the field only says how much of it, and which hue, reaches a cloud from that way. Once per pixel;
// nothing per march sample (measured: 0.99 of the time of the shader without it; at every sample it was 1.13).
//
// uShMix = 0 gives vec3(1): the old flat ambient. The coefficients are in three's order (see skyLight.ts field).
export const AMBIENT_SH_GLSL = /* glsl */`
uniform vec3 uSH[9];
uniform float uShMix;
vec3 shRel(vec3 n) {
  float x = n.x, y = n.y, z = n.z;
  vec3 r = uSH[0] * 0.886227;
  r += uSH[1] * (1.023328 * y); r += uSH[2] * (1.023328 * z); r += uSH[3] * (1.023328 * x);
  r += uSH[4] * (0.858086 * x * y); r += uSH[5] * (0.858086 * y * z); r += uSH[6] * (0.743125 * z * z - 0.247708);
  r += uSH[7] * (0.858086 * x * z); r += uSH[8] * (0.429043 * (x * x - y * y));
  return max(r, vec3(0.0));
}
/** The ambient factor for a cloud seen along d whose lit side faces up by 'up' (1 a top, 0 a base): the normal leans away from the viewer horizontally, 0.8 of the way. */
vec3 ambField(vec3 d, float up) {
  vec3 n = vec3(-0.8 * d.x, 2.0 * clamp(up, 0.0, 1.0) - 1.0, -0.8 * d.z);
  n *= inversesqrt(max(dot(n, n), 1e-6));
  return mix(vec3(1.0), shRel(n), uShMix);
}
`;
/** The uniforms' initial values (flat: the field off). */
export function ambientShUniforms(V3: new (x: number, y: number, z: number) => unknown) {
  return { uSH: { value: Array.from({ length: 9 }, (_, k) => new V3(k === 0 ? 1 / 0.886227 : 0, k === 0 ? 1 / 0.886227 : 0, k === 0 ? 1 / 0.886227 : 0)) }, uShMix: { value: 0 } };
}
